import React from "react";

import {
  CheckCircle,
  X,
  RefreshCw,
  User,
  MapPin,
  Briefcase,
  Building2,
  AlertCircle,
} from "lucide-react";

import Button from "../../../Components/Button";
import { useAccountTiers } from "../../../Utils/Helper/accountTierHelpers";
import { getInitials } from "../../../Utils/Helper/Helper";

type Props = {
  approvalsData: any[];
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
  onCancel?: (id: string) => void;
  onRefresh?: () => void;
  loading?: boolean;
  error?: string | null;
  approveRejectLoading?: boolean;
  approveRejectError?: string | null;
  processingApprovalId?: string | null;
  processingAction?: "approve" | "reject" | "cancel" | null;
  disableApproveReject?: boolean;
};

const ApprovalContainer: React.FC<Props> = ({
  approvalsData,
  onApprove,
  onReject,
  onCancel,
  onRefresh,
  loading = false,
  error = null,
  approveRejectLoading = false,
  approveRejectError = null,
  processingApprovalId = null,
  processingAction = null,
  disableApproveReject = false,
}) => {
  const { formatTier } = useAccountTiers();
  const hasPendingApprovals = approvalsData && approvalsData.length > 0;

  return (
    <div className="w-full max-w-full bg-white rounded-lg border p-3">
      {/* Header Section */}
      <div className="flex justify-between items-start mb-4 border-b border-gray-200 pb-3 bg-white">
        <div>
          <h2 className="text-base font-semibold text-gray-900 mb-2">
            Assignment Approvals & Changes
          </h2>
          <p className="text-sm text-gray-600">
            Review and approve pending executive-account assignments & changes
          </p>
        </div>
        <Button variant="white" onClick={onRefresh} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh"}
        </Button>
      </div>

      {/* Error Banner */}
      {approveRejectError && (
        <div className="flex items-start gap-3 p-3.5 mb-5 bg-red-50 border border-red-200 border-l-4 border-l-red-600 rounded-lg text-red-800 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Error:</strong> {approveRejectError}
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex flex-col items-center justify-center min-h-[400px] py-12 px-8 text-center">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          </div>
          <p className="mb-2">Loading pending approvals...</p>
        </div>
      ) : error ? (
        /* Error State */
        <div className="flex flex-col items-center justify-center min-h-[400px] py-12 px-8 text-center">
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-red-100 text-red-600 mb-6">
            <X className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-2">
            Error loading approvals
          </h3>
          <p className="text-sm text-slate-600 max-w-md">{error}</p>
        </div>
      ) : !hasPendingApprovals ? (
        /* Empty State */
        <div className="flex flex-col items-center justify-center min-h-[400px] py-12 px-8 text-center">
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-green-100 text-green-600 mb-6">
            <CheckCircle className="w-8 h-8" />
          </div>
          <h3 className="text-xl font-semibold text-slate-900 mb-2">
            No pending approvals
          </h3>
          <p className="text-sm text-slate-600 max-w-md">
            All assignments have been approved or are currently active
          </p>
        </div>
      ) : (
        /* Approvals List - Compact Grid Layout */
        <div className="flex flex-col gap-4">
          {approvalsData.map(approval => {
            const submittedDate = approval.submitted_at
              ? new Date(approval.submitted_at).toLocaleDateString(undefined, {
                  month: "short",
                  day: "2-digit",
                  year: "numeric",
                })
              : "-";

            const isPending = approval.approval_status === "pending";
            const isApproved = approval.approval_status === "approved";

            return (
              <div
                key={approval.id}
                className="bg-white border border-slate-200 rounded-xl p-4"
              >
                {/* Main Content Row */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.5fr_auto] gap-4 lg:gap-6 items-start">
                  {/* Left: Account Info */}
                  <div className="min-w-0">
                    <div className="flex items-start gap-3.5">
                      {/* Avatar with Tailwind - Lighter purple background */}
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center primary-color font-semibold text-sm">
                        {getInitials(approval.account_name || "")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start gap-2 mb-2 flex-wrap">
                          <h4 className="text-base font-bold text-slate-900 leading-snug break-words">
                            {approval.account_name || approval.customer_id}
                          </h4>
                          {/* Badge with Tailwind */}
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                              isPending
                                ? "bg-red-100 text-red-800 border border-red-200"
                                : isApproved
                                  ? "bg-green-100 text-green-800 border border-green-200"
                                  : "bg-red-100 text-red-800 border border-red-200"
                            }`}
                          >
                            {isPending
                              ? "Pending"
                              : isApproved
                                ? "Approved"
                                : "Rejected"}
                          </span>
                        </div>
                        {/* Description line for removal/change info */}
                        {(approval.change_type || approval.notes) && (
                          <p className="text-sm text-slate-600 mb-2">
                            {approval.change_type === "removal"
                              ? `Removal: ${approval.account_name || approval.customer_id} - ${submittedDate}`
                              : approval.notes || approval.batch_name || ""}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-2.5 items-center">
                          {approval.industry && (
                            <span className="flex items-center gap-1.5 text-xs text-slate-600 font-medium">
                              <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                              {approval.industry}
                            </span>
                          )}
                          {approval.tier && (
                            <span className="text-xs text-slate-600 font-medium">
                              {formatTier(approval.tier) || approval.tier}
                            </span>
                          )}
                          {!approval.industry &&
                            !approval.tier &&
                            !approval.change_type &&
                            !approval.notes && (
                              <>
                                {approval.batch_name && (
                                  <span className="text-xs text-slate-600 font-medium">
                                    {approval.batch_name}
                                  </span>
                                )}
                              </>
                            )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Middle: Executive Info */}
                  <div className="min-w-0 flex flex-row gap-3">
                    {/* Primary Executive */}
                    <div className="flex-1 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3.5">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                        <span className="text-xs font-bold text-green-800 uppercase tracking-wide">
                          PRIMARY EXECUTIVE
                        </span>
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center gap-2 text-xs">
                          <User className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                          <span className="text-slate-900 font-medium break-words leading-relaxed">
                            {approval.primary_executive_name ||
                              approval.primary_executive_id ||
                              "-"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <Briefcase className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                          <span className="text-slate-900 font-medium break-words leading-relaxed">
                            {approval.executive_role || "-"}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <MapPin className="w-3.5 h-3.5 text-green-600 flex-shrink-0" />
                          <span className="text-slate-900 font-medium break-words leading-relaxed">
                            {approval.executive_region || "-"}
                            {approval.executive_country &&
                              `, ${approval.executive_country}`}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Secondary Executive */}
                    {approval.secondary_executive_name ||
                    approval.secondary_executive_id ? (
                      <div className="flex-1 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3.5">
                        <div className="flex items-center gap-2 mb-3">
                          <CheckCircle className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <span className="text-xs font-bold text-blue-800 uppercase tracking-wide">
                            SECONDARY EXECUTIVE
                          </span>
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center gap-2 text-xs">
                            <User className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                            <span className="text-slate-900 font-medium break-words leading-relaxed">
                              {approval.secondary_executive_name ||
                                approval.secondary_executive_id ||
                                "-"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <Briefcase className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                            <span className="text-slate-900 font-medium break-words leading-relaxed">
                              {approval.secondary_executive_role ||
                                approval.executive_role ||
                                "-"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs">
                            <MapPin className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                            <span className="text-slate-900 font-medium break-words leading-relaxed">
                              {approval.secondary_executive_region ||
                                approval.executive_region ||
                                "-"}
                              {approval.secondary_executive_country &&
                                `, ${approval.secondary_executive_country}`}
                              {!approval.secondary_executive_country &&
                                approval.executive_country &&
                                `, ${approval.executive_country}`}
                            </span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1"></div>
                    )}
                  </div>

                  {/* Right: Actions & Metadata */}
                  <div className="flex flex-col gap-3 items-stretch lg:items-end min-w-0 lg:min-w-[140px]">
                    {isPending && (
                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          onClick={() => onApprove && onApprove(approval.id)}
                          disabled={
                            disableApproveReject ||
                            (processingApprovalId === approval.id &&
                              processingAction === "approve")
                          }
                          icon={
                            processingApprovalId === approval.id &&
                            processingAction === "approve" ? (
                              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <CheckCircle className="w-3.5 h-3.5" />
                            )
                          }
                        >
                          {processingApprovalId === approval.id &&
                          processingAction === "approve"
                            ? "Approving..."
                            : "Approve"}
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => onReject && onReject(approval.id)}
                          disabled={
                            disableApproveReject ||
                            (processingApprovalId === approval.id &&
                              processingAction === "reject")
                          }
                          icon={
                            processingApprovalId === approval.id &&
                            processingAction === "reject" ? (
                              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <X className="w-3.5 h-3.5" />
                            )
                          }
                        >
                          {processingApprovalId === approval.id &&
                          processingAction === "reject"
                            ? "Rejecting..."
                            : "Reject"}
                        </Button>
                        {onCancel && (
                          <Button
                            variant="gray"
                            onClick={() => onCancel(approval.id)}
                            disabled={
                              processingApprovalId === approval.id &&
                              processingAction === "cancel"
                            }
                            icon={
                              processingApprovalId === approval.id &&
                              processingAction === "cancel" ? (
                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <X className="w-3.5 h-3.5" />
                              )
                            }
                            className="w-full justify-center px-4 py-2.5 text-xs font-semibold rounded-lg bg-slate-100 text-slate-600 border border-slate-300 hover:bg-slate-200 hover:border-slate-400 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md active:translate-y-0"
                          >
                            {processingApprovalId === approval.id &&
                            processingAction === "cancel"
                              ? "Canceling..."
                              : "Cancel"}
                          </Button>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 pt-2 border-t border-slate-200 w-full">
                      <span className="text-xs text-slate-600 font-medium leading-relaxed">
                        {approval.submitted_by_name && (
                          <> • {approval.submitted_by_name}</>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApprovalContainer;
